import React from "react";
import {
  BuildingOffice2Icon,
  PaperAirplaneIcon,
  TicketIcon,
} from "@heroicons/react/24/outline";
import { formatPrice } from "@/lib/general";
import useTripStore from "@/stores/tripBuilder/useTrip";
import useCurrencyStore from "@/stores/ui/currencyStore";

const COLORS = {
  accommodation: "#FF6B6B", // Vibrant coral red
  transport: "#4ECDC4", // Bright turquoise
  activities: "#FFD93D", // Sunny yellow
};

type CategoryKey = "transport" | "accommodation" | "activities";

interface CategoryConfig {
  key: CategoryKey;
  icon: React.ReactNode;
  label: string;
  color: string;
}

const CATEGORIES: CategoryConfig[] = [
  {
    key: "accommodation",
    icon: (
      <BuildingOffice2Icon
        className="w-4 h-4"
        style={{ color: COLORS.accommodation }}
      />
    ),
    label: "Accommodation",
    color: COLORS.accommodation,
  },
  {
    key: "transport",
    icon: (
      <PaperAirplaneIcon
        className="w-4 h-4"
        style={{ color: COLORS.transport }}
      />
    ),
    label: "Transport",
    color: COLORS.transport,
  },
  {
    key: "activities",
    icon: (
      <TicketIcon className="w-4 h-4" style={{ color: COLORS.activities }} />
    ),
    label: "Activities",
    color: COLORS.activities,
  },
];

const BudgetCategories: React.FC = () => {
  const selectedCurrency = useCurrencyStore((state) => state.selectedCurrency);
  const selectedLocationBudget = useTripStore(
    (state) => state.selectedLocationBudget
  );

  return (
    <div className="flex flex-col w-full gap-1.5 overflow-hidden h-full">
      {CATEGORIES.map((category) => (
        <div
          key={category.key}
          className="justify-between bg-white rounded-xl p-2 flex flex-col flex-grow"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div
                className="p-2 rounded-lg"
                style={{ backgroundColor: `${category.color}15` }}
              >
                {category.icon}
              </div>
              <span className="text-sm font-normal text-neutral-800">
                {category.label}
              </span>
            </div>
          </div>

          <div className="flex flex-col gap-1 pb-1 mt-auto">
            <div className="flex items-center gap-3">
              <p className="text-base font-light text-neutral-900">
                {formatPrice(
                  selectedCurrency.code,
                  selectedLocationBudget[category.key]
                )}
              </p>
              <div
                className="h-1 w-1 rounded-full"
                style={{ backgroundColor: category.color }}
              />
              <p className="text-xs 2xl:text-sm text-neutral-500 font-light">
                {selectedLocationBudget.total > 0
                  ? Math.round(
                      (selectedLocationBudget[category.key] /
                        selectedLocationBudget.total) *
                        100
                    )
                  : 0}
                %
              </p>
            </div>
            <div className="hidden xl:block mt-2 w-full bg-neutral-100 rounded-full h-1.5">
              <div
                className="h-full rounded-full transition-all duration-300"
                style={{
                  width: `${
                    selectedLocationBudget.total > 0
                      ? Math.round(
                          (selectedLocationBudget[category.key] /
                            selectedLocationBudget.total) *
                            100
                        )
                      : 0
                  }%`,
                  backgroundColor: category.color,
                }}
              />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default BudgetCategories;
